/**
 * Hooks de autenticación - TanStack Query
 * Encapsulan todas las llamadas a la API de auth
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import apiClient from '@/lib/apiClient'
import { useAuthStore, type FirmSession } from '@/store/authStore'
import type { LoginRequest, LoginResponse, User, UiPreferences } from '@/types'

// ─── Types ────────────────────────────────────────────────────────────────────
export interface SSOConfig {
  auth_mode: 'password_only' | 'sso_only' | 'both'
  google_enabled: boolean
  saml_enabled: boolean
  google_client_id: string
  /** El portal del colaborador hereda la política del tenant (ver employee_portal/sso.py). */
  portal_sso_enabled?: boolean
  portal_google_enabled?: boolean
  portal_password_login_allowed?: boolean
}

// ─── Keys de cache ────────────────────────────────────────────────────────────
export const authKeys = {
  me: ['auth', 'me'] as const,
  tenants: ['auth', 'tenants'] as const,
  ssoConfig: ['auth', 'sso-config'] as const,
}

// ─── AUT-01: Login ────────────────────────────────────────────────────────────
export function useLogin() {
  const { setTokens, setTenants, setFirm } = useAuthStore()

  return useMutation({
    mutationFn: async (credentials: LoginRequest): Promise<LoginResponse> => {
      const { data } = await apiClient.post<LoginResponse>('/auth/token/', credentials)
      return data
    },
    onSuccess: (data) => {
      // JWT slim: solo actualiza identidad. Los tenants (con permisos) vienen en el response body.
      setTokens(data.access)
      if (data.tenants?.length) {
        setTenants(data.tenants)
      }
      // Señal "Estudio Contable" (null si el usuario no pertenece a un estudio).
      setFirm((data as { firm?: FirmSession | null }).firm ?? null)
    },
  })
}

// ─── Logout ───────────────────────────────────────────────────────────────────
export function useLogout() {
  const { logout } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Refresh token is sent automatically via httpOnly cookie
      await apiClient.post('/auth/logout/').catch(() => {})
    },
    onSettled: () => {
      logout()
      queryClient.clear()
      navigate('/auth/login', { replace: true })
    },
  })
}

// ─── Perfil del usuario actual ────────────────────────────────────────────────
export function useMe() {
  const { isAuthenticated, setEmployeeRecord, setFirm } = useAuthStore()

  return useQuery({
    queryKey: authKeys.me,
    queryFn: async (): Promise<User> => {
      const { data } = await apiClient.get<User>('/auth/me/')
      // Caso B del portal: si el backend retorna employee_record, sincronizar al store.
      if (typeof (data as any).employee_record !== 'undefined') {
        setEmployeeRecord((data as any).employee_record ?? null)
      }
      // Estudio Contable: la cookie zen_session NO trae `firm` (solo user+tenants),
      // así que /auth/me lo rehidrata en cada carga/cambio de subdominio.
      if (typeof (data as any).firm !== 'undefined') {
        setFirm((data as any).firm ?? null)
      }
      return data
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutos
  })
}

// ─── Preferencias de UI del usuario (persisten en el backend) ─────────────────
export function useUpdateUiPreference() {
  const qc = useQueryClient()
  return useMutation({
    // PATCH parcial: el backend MERGEA ui_preferences (no pisa otras claves).
    mutationFn: async (prefs: Partial<UiPreferences>): Promise<User> => {
      const { data } = await apiClient.patch<User>('/auth/me/', { ui_preferences: prefs })
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData(authKeys.me, (prev: User | undefined) =>
        prev ? { ...prev, ui_preferences: data.ui_preferences } : data,
      )
    },
  })
}

// ─── USR-03: Cambiar contraseña ───────────────────────────────────────────────
export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: {
      current_password: string
      new_password: string
      confirm_password: string
    }) => {
      const response = await apiClient.post('/auth/change-password/', data)
      return response.data
    },
  })
}

// ─── Workspace Lookup (app.clobi.io) ─────────────────────────────────────────
export interface WorkspaceLookupResult {
  name: string
  schema: string
  domain: string
}

export function useWorkspaceLookup() {
  // MED-2: la respuesta ya NO incluye la lista de workspaces (evita enumeración).
  // Si el email existe, los espacios se envían por correo.
  return useMutation({
    mutationFn: async (email: string): Promise<{ detail: string }> => {
      const { data } = await apiClient.post('/auth/workspace-lookup/', { email })
      return data
    },
  })
}

// ─── Recuperación de contraseña ───────────────────────────────────────────────
export function usePasswordResetRequest() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { data } = await apiClient.post('/auth/password-reset/', { email })
      return data
    },
  })
}

export function usePasswordResetConfirm() {
  return useMutation({
    mutationFn: async (payload: {
      uid: string
      token: string
      new_password: string
    }) => {
      const { data } = await apiClient.post('/auth/password-reset/confirm/', payload)
      return data
    },
  })
}

// ─── Aceptar invitación ───────────────────────────────────────────────────────
export function useAcceptInvitation() {
  return useMutation({
    mutationFn: async (payload: { token: string; new_password: string }) => {
      const { data } = await apiClient.post('/auth/accept-invitation/', payload)
      return data
    },
  })
}

// ─── SSO: Configuración pública ──────────────────────────────────────────────
export function useSSOConfig() {
  return useQuery<SSOConfig>({
    queryKey: authKeys.ssoConfig,
    queryFn: async () => {
      const { data } = await apiClient.get<SSOConfig>('/auth/sso-config/')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

// ─── SSO: Login con Google (flujo directo, solo dev) ─────────────────────────
export function useGoogleLogin() {
  const { setTokens, setTenants, setFirm } = useAuthStore()

  return useMutation({
    mutationFn: async (payload: { code: string; state: string }): Promise<LoginResponse> => {
      const { data } = await apiClient.post<LoginResponse>('/auth/google/', payload)
      return data
    },
    onSuccess: (data) => {
      // JWT slim: identidad del token + tenants/permisos del response body
      setTokens(data.access)
      if (data.tenants?.length) {
        setTenants(data.tenants)
      }
      setFirm((data as { firm?: FirmSession | null }).firm ?? null)
    },
  })
}

// ─── SSO: Canjear OTP del callback del backend (staging/prod) ────────────────
export function useGoogleOTPRedeem() {
  const { setTokens, setTenants, setFirm } = useAuthStore()

  return useMutation({
    mutationFn: async (payload: { token: string }): Promise<LoginResponse> => {
      const { data } = await apiClient.get<LoginResponse>('/auth/sso/google/otp/', {
        params: payload,
      })
      return data
    },
    onSuccess: (data) => {
      setTokens(data.access)
      if (data.tenants?.length) {
        setTenants(data.tenants)
      }
      setFirm((data as { firm?: FirmSession | null }).firm ?? null)
    },
  })
}
